// File: src/utils/toastUtils.js

/**
 * ============================================
 * Toast Utilities + Toast Manager
 * ============================================
 *
 * نظام موحد لإدارة جميع الـ Toasts في المشروع:
 * - Queue system
 * - Priority
 * - Grouping
 * - Debounce
 *
 * الألوان:
 * - الذهبي البرتقالي: #ffb522 (لون المتجر الرئيسي)
 * - الأحمر: #ef4444 (للمفضلة)
 * ============================================
 */

import React from "react";
import { toast } from "react-toastify";
import {
  ShoppingCart,
  Heart,
  AlertCircle,
  Info,
  Trash2,
  Package,
  User,
  Mail,
  CreditCard,
  Truck,
  Sparkles,
  Bell,
  Check,
} from "lucide-react";

// ============================================
// إعدادات عامة
// ============================================

const isClient = typeof window !== "undefined";
const isRTL = () => isClient && document.dir === "rtl";
const isArabic = () => isRTL();

const DEFAULT_OPTIONS = {
  position: isRTL() ? "top-left" : "top-right",
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  rtl: isRTL(),
  icon: false,
};

// ============================================
// الأولويات
// ============================================

export const TOAST_PRIORITY = {
  LOW: 1,
  NORMAL: 2,
  HIGH: 3,
  CRITICAL: 4,
};

const KIND_PRIORITY = {
  cart: TOAST_PRIORITY.NORMAL,
  wishlist: TOAST_PRIORITY.NORMAL,
  success: TOAST_PRIORITY.NORMAL,
  info: TOAST_PRIORITY.LOW,
  warning: TOAST_PRIORITY.HIGH,
  error: TOAST_PRIORITY.CRITICAL,
  login: TOAST_PRIORITY.NORMAL,
  logout: TOAST_PRIORITY.NORMAL,
  email: TOAST_PRIORITY.NORMAL,
  order: TOAST_PRIORITY.HIGH,
  payment: TOAST_PRIORITY.HIGH,
  shipping: TOAST_PRIORITY.NORMAL,
  promo: TOAST_PRIORITY.NORMAL,
  system: TOAST_PRIORITY.HIGH,
  system_notification: TOAST_PRIORITY.CRITICAL,
  checkout: TOAST_PRIORITY.HIGH,
};

// ============================================
// مدير التوستات
// ============================================

let activeToastsCount = 0;
const MAX_VISIBLE_TOASTS = 3;

const toastQueue = [];
const debounceMap = new Map();
const queueGroupMap = new Map();

const DEFAULT_DEBOUNCE_MS = 1200;
const DEFAULT_GROUP_WINDOW_MS = 1500;

const now = () => Date.now();

const createToastId = (prefix = "toast") =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const getToastTypeMethod = (toastType) => {
  switch (toastType) {
    case "success":
      return toast.success;
    case "error":
      return toast.error;
    case "warning":
      return toast.warning;
    default:
      return toast.info;
  }
};

const canPassDebounce = (debounceKey, debounceMs = DEFAULT_DEBOUNCE_MS) => {
  if (!debounceKey) return true;

  const currentTime = now();
  const lastTime = debounceMap.get(debounceKey) || 0;

  if (currentTime - lastTime < debounceMs) {
    return false;
  }

  debounceMap.set(debounceKey, currentTime);
  return true;
};

const removeFromGroupMapIfNeeded = (item) => {
  if (!item?.groupKey) return;

  const mapped = queueGroupMap.get(item.groupKey);
  if (mapped && mapped.id === item.id) {
    queueGroupMap.delete(item.groupKey);
  }
};

const sortQueue = () => {
  toastQueue.sort((a, b) => {
    if ((b.priority || 0) !== (a.priority || 0)) {
      return (b.priority || 0) - (a.priority || 0);
    }
    return (a.createdAt || 0) - (b.createdAt || 0);
  });
};

const showNextToast = () => {
  while (activeToastsCount < MAX_VISIBLE_TOASTS && toastQueue.length > 0) {
    const item = toastQueue.shift();
    removeFromGroupMapIfNeeded(item);

    if (!item) return;

    activeToastsCount += 1;

    const toastFn = getToastTypeMethod(item.toastType);
    const content =
      typeof item.render === "function" ? item.render(item) : item.content;

    const originalOnClose = item.options?.onClose;

    toastFn(content, {
      ...DEFAULT_OPTIONS,
      ...(item.options || {}),
      toastId: item.toastId || item.id,
      onClose: (...args) => {
        activeToastsCount = Math.max(0, activeToastsCount - 1);

        if (typeof originalOnClose === "function") {
          originalOnClose(...args);
        }

        showNextToast();
      },
    });
  }
};

export const enqueueToast = (config = {}) => {
  const id = config.id || createToastId(config.kind || "toast");
  const priority =
    config.priority ??
    KIND_PRIORITY[config.kind] ??
    TOAST_PRIORITY.NORMAL;

  const debounceKey = config.debounceKey || null;
  const debounceMs = config.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const groupKey = config.groupKey || null;
  const groupWindowMs = config.groupWindowMs ?? DEFAULT_GROUP_WINDOW_MS;
  const createdAt = now();

  if (!canPassDebounce(debounceKey, debounceMs)) {
    return null;
  }

  if (groupKey) {
    const existing = queueGroupMap.get(groupKey);

    if (
      existing &&
      createdAt - (existing.lastGroupedAt || existing.createdAt || 0) <= groupWindowMs
    ) {
      existing.groupedCount = (existing.groupedCount || 1) + 1;
      existing.lastGroupedAt = createdAt;

      if (typeof config.merge === "function") {
        config.merge(existing, config);
      }

      sortQueue();
      return existing.id;
    }
  }

  const item = {
    id,
    kind: config.kind || "info",
    toastType: config.toastType || "info",
    priority,
    createdAt,
    lastGroupedAt: createdAt,
    groupedCount: 1,
    debounceKey,
    debounceMs,
    groupKey,
    groupWindowMs,
    render: config.render,
    content: config.content,
    options: config.options || {},
    toastId: config.toastId || id,
  };

  toastQueue.push(item);

  if (groupKey) {
    queueGroupMap.set(groupKey, item);
  }

  sortQueue();
  showNextToast();

  return id;
};

// ============================================
// مكون Toast مخصص
// ============================================

export const CustomToast = ({
  icon: Icon,
  title,
  message,
  iconColor,
  bgColor,
  groupedCount = 1,
}) => {
  const rtl = isRTL();

  const finalMessage =
    groupedCount > 1
      ? `${message}${message ? " " : ""}(x${groupedCount})`
      : message;

  return (
    <div
      className={`custom-toast-content ${rtl ? "rtl" : ""}`}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        direction: rtl ? "rtl" : "ltr",
      }}
    >
      {Icon && (
        <div
          className="toast-icon-wrapper"
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            background: bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={18} color={iconColor} strokeWidth={2} />
        </div>
      )}

      <div className="toast-text-content" style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div
            className="toast-title"
            style={{
              fontSize: "0.9rem",
              fontWeight: 700,
              color: "#0d1b3e",
              marginBottom: finalMessage ? "4px" : 0,
              lineHeight: 1.3,
            }}
          >
            {title}
          </div>
        )}

        {finalMessage && (
          <div
            className="toast-message"
            style={{
              fontSize: "0.8rem",
              color: "#5a6478",
              lineHeight: 1.4,
            }}
          >
            {finalMessage}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// Helper لعرض Toast موحد
// ============================================

const queueCustomToast = ({
  kind,
  toastType,
  title,
  message,
  icon,
  iconColor,
  bgColor,
  className,
  options = {},
  debounceKey,
  debounceMs,
  groupKey,
  groupWindowMs,
  priority,
}) => {
  return enqueueToast({
    kind,
    toastType,
    priority,
    debounceKey,
    debounceMs,
    groupKey,
    groupWindowMs,
    options: {
      ...options,
      rtl: isRTL(),
      className,
      icon: false,
    },
    render: (item) => (
      <CustomToast
        icon={icon}
        title={title}
        message={message}
        iconColor={iconColor}
        bgColor={bgColor}
        groupedCount={item.groupedCount || 1}
      />
    ),
  });
};

// ============================================
// ✅ إشعار إضافة للسلة
// ============================================

export const showCartAdd = (productName, options = {}) => {
  const ar = isArabic();

  return queueCustomToast({
    kind: "cart",
    toastType: "success",
    title: ar ? "تمت الإضافة للسلة" : "Added to Cart",
    message: ar
      ? `تم إضافة "${productName}" إلى سلة التسوق`
      : `"${productName}" added to your cart`,
    icon: ShoppingCart,
    iconColor: "#b87d0a",
    bgColor: "rgba(255, 181, 34, 0.2)",
    className: "custom-toast custom-toast--cart",
    debounceKey: `cart-add-${productName}`,
    groupKey: `cart-add-${productName}`,
    options: {
      autoClose: 2500,
      ...options,
    },
  });
};

// ============================================
// ✅ إشعار إزالة من السلة
// ============================================

export const showCartRemove = (productName, options = {}) => {
  const ar = isArabic();

  return queueCustomToast({
    kind: "cart",
    toastType: "info",
    title: ar ? "تمت الإزالة" : "Removed",
    message: ar
      ? `تم إزالة "${productName}" من السلة`
      : `"${productName}" removed from cart`,
    icon: Trash2,
    iconColor: "#6b7280",
    bgColor: "rgba(107, 114, 128, 0.15)",
    className: "custom-toast custom-toast--cart-remove",
    debounceKey: `cart-remove-${productName}`,
    groupKey: `cart-remove-${productName}`,
    options: {
      autoClose: 2000,
      ...options,
    },
  });
};

// ============================================
// ❤️ إشعار إضافة للمفضلة
// ============================================

export const showWishlistAdd = (productName, options = {}) => {
  const ar = isArabic();

  return queueCustomToast({
    kind: "wishlist",
    toastType: "success",
    title: ar ? "تمت الإضافة للمفضلة" : "Added to Favorites",
    message: ar
      ? `تم إضافة "${productName}" إلى قائمة المفضلة`
      : `"${productName}" added to your favorites`,
    icon: Heart,
    iconColor: "#dc2626",
    bgColor: "rgba(220, 38, 38, 0.15)",
    className: "custom-toast custom-toast--wishlist",
    debounceKey: `wishlist-add-${productName}`,
    groupKey: `wishlist-add-${productName}`,
    options: {
      autoClose: 2500,
      ...options,
    },
  });
};

// ============================================
// ❤️ إشعار إزالة من المفضلة
// ============================================

export const showWishlistRemove = (productName, options = {}) => {
  const ar = isArabic();

  return queueCustomToast({
    kind: "wishlist",
    toastType: "info",
    title: ar ? "تمت الإزالة من المفضلة" : "Removed from Favorites",
    message: ar
      ? `تم إزالة "${productName}" من قائمة المفضلة`
      : `"${productName}" removed from your favorites`,
    icon: Heart,
    iconColor: "#6b7280",
    bgColor: "rgba(107, 114, 128, 0.15)",
    className: "custom-toast custom-toast--wishlist-remove",
    debounceKey: `wishlist-remove-${productName}`,
    groupKey: `wishlist-remove-${productName}`,
    options: {
      autoClose: 2000,
      ...options,
    },
  });
};

// ============================================
// ✅ إشعار نجاح عام
// ============================================

export const showSuccess = (title, message, options = {}) => {
  return queueCustomToast({
    kind: "success",
    toastType: "success",
    title,
    message,
    icon: Check,
    iconColor: "#16a34a",
    bgColor: "rgba(34, 197, 94, 0.15)",
    className: "custom-toast custom-toast--success",
    debounceKey: `success-${title}-${message}`,
    groupKey: `success-${title}-${message}`,
    options,
  });
};

// ============================================
// ❌ إشعار خطأ
// ============================================

export const showError = (title, message, options = {}) => {
  return queueCustomToast({
    kind: "error",
    toastType: "error",
    title,
    message,
    icon: AlertCircle,
    iconColor: "#dc2626",
    bgColor: "rgba(239, 68, 68, 0.15)",
    className: "custom-toast custom-toast--error",
    debounceKey: `error-${title}-${message}`,
    groupKey: `error-${title}-${message}`,
    priority: TOAST_PRIORITY.CRITICAL,
    options,
  });
};

// ============================================
// ⚠️ إشعار تحذير
// ============================================

export const showWarning = (title, message, options = {}) => {
  return queueCustomToast({
    kind: "warning",
    toastType: "warning",
    title,
    message,
    icon: AlertCircle,
    iconColor: "#ca8a04",
    bgColor: "rgba(234, 179, 8, 0.15)",
    className: "custom-toast custom-toast--warning",
    debounceKey: `warning-${title}-${message}`,
    groupKey: `warning-${title}-${message}`,
    priority: TOAST_PRIORITY.HIGH,
    options,
  });
};

// ============================================
// ℹ️ إشعار معلومات
// ============================================

export const showInfo = (title, message, options = {}) => {
  return queueCustomToast({
    kind: "info",
    toastType: "info",
    title,
    message,
    icon: Info,
    iconColor: "#2563eb",
    bgColor: "rgba(59, 130, 246, 0.15)",
    className: "custom-toast custom-toast--info",
    debounceKey: `info-${title}-${message}`,
    groupKey: `info-${title}-${message}`,
    options,
  });
};

// ============================================
// 👤 إشعار تسجيل الدخول
// ============================================

export const showLoginSuccess = (userName, options = {}) => {
  const ar = isArabic();

  return queueCustomToast({
    kind: "login",
    toastType: "success",
    title: ar ? "مرحباً بك!" : "Welcome!",
    message: ar
      ? `سعداء بعودتك${userName ? `، ${userName}` : ""}`
      : `Welcome back${userName ? `, ${userName}` : ""}!`,
    icon: User,
    iconColor: "#16a34a",
    bgColor: "rgba(34, 197, 94, 0.15)",
    className: "custom-toast custom-toast--login",
    debounceKey: `login-${userName || "user"}`,
    groupKey: `login-${userName || "user"}`,
    options: {
      autoClose: 3000,
      ...options,
    },
  });
};

// ============================================
// 👋 إشعار تسجيل الخروج
// ============================================

export const showLogout = (options = {}) => {
  const ar = isArabic();

  return queueCustomToast({
    kind: "logout",
    toastType: "info",
    title: ar ? "تم تسجيل الخروج" : "Logged Out",
    message: ar
      ? "تم تسجيل خروجك بنجاح"
      : "You have been logged out successfully",
    icon: User,
    iconColor: "#6b7280",
    bgColor: "rgba(107, 114, 128, 0.15)",
    className: "custom-toast custom-toast--logout",
    debounceKey: "logout",
    groupKey: "logout",
    options: {
      autoClose: 2000,
      ...options,
    },
  });
};

// ============================================
// 📧 إشعار إرسال البريد
// ============================================

export const showEmailSent = (email, options = {}) => {
  const ar = isArabic();

  return queueCustomToast({
    kind: "email",
    toastType: "success",
    title: ar ? "تم إرسال البريد" : "Email Sent",
    message: ar
      ? `تم إرسال رسالة إلى ${email || "بريدك الإلكتروني"}`
      : `Email sent to ${email || "your email address"}`,
    icon: Mail,
    iconColor: "#2563eb",
    bgColor: "rgba(59, 130, 246, 0.15)",
    className: "custom-toast custom-toast--email",
    debounceKey: `email-${email || "default"}`,
    groupKey: `email-${email || "default"}`,
    options: {
      autoClose: 4000,
      ...options,
    },
  });
};

// ============================================
// 📦 إشعار إنشاء طلب
// ============================================

export const showOrderCreated = (orderId, options = {}) => {
  const ar = isArabic();

  return queueCustomToast({
    kind: "order",
    toastType: "success",
    title: ar ? "تم إنشاء الطلب" : "Order Placed",
    message: ar
      ? `تم إنشاء طلبك رقم #${orderId} بنجاح`
      : `Your order #${orderId} has been placed successfully`,
    icon: Package,
    iconColor: "#16a34a",
    bgColor: "rgba(34, 197, 94, 0.15)",
    className: "custom-toast custom-toast--order",
    debounceKey: `order-${orderId}`,
    groupKey: `order-${orderId}`,
    priority: TOAST_PRIORITY.HIGH,
    options: {
      autoClose: 5000,
      ...options,
    },
  });
};

// ============================================
// 💳 إشعار دفع ناجح
// ============================================

export const showPaymentSuccess = (amount, options = {}) => {
  const ar = isArabic();

  return queueCustomToast({
    kind: "payment",
    toastType: "success",
    title: ar ? "تم الدفع بنجاح" : "Payment Successful",
    message: ar
      ? `تم معالجة دفع بقيمة $${amount}`
      : `Payment of $${amount} processed successfully`,
    icon: CreditCard,
    iconColor: "#16a34a",
    bgColor: "rgba(34, 197, 94, 0.15)",
    className: "custom-toast custom-toast--payment",
    debounceKey: `payment-${amount}`,
    groupKey: `payment-${amount}`,
    priority: TOAST_PRIORITY.HIGH,
    options: {
      autoClose: 4000,
      ...options,
    },
  });
};

// ============================================
// 🚚 إشعار شحن
// ============================================

export const showShippingUpdate = (status, options = {}) => {
  const ar = isArabic();

  const statusMessages = {
    shipped: ar ? "تم شحن الطلب" : "Order Shipped",
    delivered: ar ? "تم التوصيل" : "Order Delivered",
    processing: ar ? "جاري المعالجة" : "Processing",
  };

  return queueCustomToast({
    kind: "shipping",
    toastType: "info",
    title: statusMessages[status] || statusMessages.processing,
    message: ar
      ? "سنقوم بإعلامك بأي تحديثات جديدة"
      : "We will notify you of any updates",
    icon: Truck,
    iconColor: "#2563eb",
    bgColor: "rgba(59, 130, 246, 0.15)",
    className: "custom-toast custom-toast--shipping",
    debounceKey: `shipping-${status}`,
    groupKey: `shipping-${status}`,
    options: {
      autoClose: 4000,
      ...options,
    },
  });
};

// ============================================
// ✨ إشعار عرض ترويجي
// ============================================

export const showPromo = (title, code, options = {}) => {
  const ar = isArabic();

  return queueCustomToast({
    kind: "promo",
    toastType: "info",
    title: title || (ar ? "عرض خاص!" : "Special Offer!"),
    message: code
      ? ar
        ? `استخدم الكود: ${code}`
        : `Use code: ${code}`
      : ar
      ? "لا تفوت هذا العرض المميز"
      : "Don't miss this special offer",
    icon: Sparkles,
    iconColor: "#b87d0a",
    bgColor: "rgba(255, 181, 34, 0.2)",
    className: "custom-toast custom-toast--promo",
    debounceKey: `promo-${title || "promo"}-${code || "none"}`,
    groupKey: `promo-${title || "promo"}-${code || "none"}`,
    options: {
      autoClose: 6000,
      ...options,
    },
  });
};

// ============================================
// 🔔 إشعار نظام عام
// ============================================

export const showSystemNotification = (title, message, options = {}) => {
  return queueCustomToast({
    kind: "system",
    toastType: "info",
    title,
    message,
    icon: Bell,
    iconColor: "#b87d0a",
    bgColor: "rgba(255, 181, 34, 0.15)",
    className: "custom-toast custom-toast--system",
    debounceKey: `system-${title}-${message}`,
    groupKey: `system-${title}-${message}`,
    priority: TOAST_PRIORITY.HIGH,
    options: {
      autoClose: 5000,
      ...options,
    },
  });
};

// ============================================
// دوال مساعدة
// ============================================

export const dismissAllToasts = () => {
  toast.dismiss();
};

export const updateToast = (toastId, options) => {
  toast.update(toastId, options);
};

export const showLoading = (message, options = {}) => {
  const ar = isArabic();

  return toast.loading(message || (ar ? "جاري المعالجة..." : "Processing..."), {
    ...DEFAULT_OPTIONS,
    ...options,
    rtl: isRTL(),
  });
};

// ============================================
// Export الافتراضي
// ============================================

const toastUtils = {
  enqueue: enqueueToast,
  success: showSuccess,
  error: showError,
  warning: showWarning,
  info: showInfo,
  cartAdd: showCartAdd,
  cartRemove: showCartRemove,
  wishlistAdd: showWishlistAdd,
  wishlistRemove: showWishlistRemove,
  loginSuccess: showLoginSuccess,
  logout: showLogout,
  emailSent: showEmailSent,
  orderCreated: showOrderCreated,
  paymentSuccess: showPaymentSuccess,
  shippingUpdate: showShippingUpdate,
  promo: showPromo,
  systemNotification: showSystemNotification,
  loading: showLoading,
  dismissAll: dismissAllToasts,
  update: updateToast,
};

export default toastUtils;